import {
  degreeInterestOptions,
  focusOptions,
  specializationOptions,
  type SpecializationOption,
} from '../data/degrees'

/**
 * Every degree level × specialization combination has a landing page at
 * `/find-degrees/{degree}-{mProgram}`, e.g.
 * `/find-degrees/bachelors-business-administration`.
 *
 * The slug is readable rather than numeric so the URL carries the keyword, but
 * the numeric form (`/find-degrees/3-102`) resolves too, since campaign links
 * are often built from the raw codes.
 */

export interface DegreeProgramCombo {
  slug: string
  /** degreeInterest code, e.g. "3" */
  degreeInterest: string
  /** areaStudy code, e.g. "1" */
  areaStudy: string
  /** mProgram code, e.g. "102" */
  mProgram: string
  degreeLabel: string
  areaLabel: string
  programLabel: string
  /** "Bachelors in Business Administration" */
  title: string
}

export function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

const degreeBySlug = new Map<string, string>()
const degreeByCode = new Map<string, string>()
for (const option of degreeInterestOptions) {
  degreeBySlug.set(slugify(option.type), option.value)
  degreeByCode.set(option.value, option.type)
}

const areaByCode = new Map<string, string>()
for (const option of focusOptions) {
  areaByCode.set(option.value, option.type)
}

/**
 * Specializations repeat a few labels across areas (Graphic Design appears
 * under both Media and Visual Arts), so the slug is only unique within its
 * area. Later duplicates get their area appended to keep every URL distinct.
 */
const programSlugByCode = new Map<string, string>()
const programByProgramSlug = new Map<string, SpecializationOption>()
for (const option of specializationOptions) {
  let slug = slugify(option.type)
  if (programByProgramSlug.has(slug)) {
    const area = areaByCode.get(option.focusValue) || option.focusValue
    slug = `${slug}-${slugify(area)}`
  }
  programSlugByCode.set(option.value, slug)
  programByProgramSlug.set(slug, option)
}

function buildCombo(
  degreeCode: string,
  program: SpecializationOption,
): DegreeProgramCombo {
  const degreeLabel = degreeByCode.get(degreeCode) || ''
  const areaLabel = areaByCode.get(program.focusValue) || ''
  const programSlug = programSlugByCode.get(program.value) || ''
  return {
    slug: `${slugify(degreeLabel)}-${programSlug}`,
    degreeInterest: degreeCode,
    areaStudy: program.focusValue,
    mProgram: program.value,
    degreeLabel,
    areaLabel,
    programLabel: program.type,
    title: `${degreeLabel} in ${program.type}`,
  }
}

/** Every valid degree × program combination, in menu order. */
export const degreeProgramCombos: DegreeProgramCombo[] =
  degreeInterestOptions.flatMap((degree) =>
    specializationOptions.map((program) => buildCombo(degree.value, program)),
  )

const comboBySlug = new Map<string, DegreeProgramCombo>()
for (const combo of degreeProgramCombos) {
  comboBySlug.set(combo.slug, combo)
}

/**
 * Resolves a `/find-degrees/` slug to its combination. Accepts the readable
 * form (`bachelors-business-administration`) and the numeric code form
 * (`3-102`); anything else returns undefined so the caller can fall back.
 */
export function getDegreeProgramCombo(
  slug: string | undefined,
): DegreeProgramCombo | undefined {
  if (!slug) return undefined
  const normalized = slug.toLowerCase().trim()

  const readable = comboBySlug.get(normalized)
  if (readable) return readable

  // Numeric form: {degreeInterest}-{mProgram}
  const numeric = normalized.match(/^(\d{1,2})-(\d{3,4})$/)
  if (numeric) {
    const [, degreeCode, programCode] = numeric
    if (!degreeByCode.has(degreeCode)) return undefined
    const program = specializationOptions.find((p) => p.value === programCode)
    if (!program) return undefined
    return buildCombo(degreeCode, program)
  }

  return undefined
}

/** Combos sharing this program at other degree levels. */
export function getRelatedLevels(
  combo: DegreeProgramCombo,
): DegreeProgramCombo[] {
  return degreeProgramCombos.filter(
    (c) =>
      c.mProgram === combo.mProgram &&
      c.degreeInterest !== combo.degreeInterest,
  )
}

/** Other programs in the same area at the same degree level. */
export function getRelatedPrograms(
  combo: DegreeProgramCombo,
  limit = 6,
): DegreeProgramCombo[] {
  return degreeProgramCombos
    .filter(
      (c) =>
        c.areaStudy === combo.areaStudy &&
        c.degreeInterest === combo.degreeInterest &&
        c.mProgram !== combo.mProgram,
    )
    .slice(0, limit)
}
